'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { Button } from './components/ui/button';
import {
	CheckCircle2,
	Box,
	BarChart3,
	Truck,
	ClipboardCheck,
	Thermometer,
	PackageSearch,
	Tags,
	Globe2,
	Users2,
	Shield,
	FileCheck,
} from 'lucide-react';

const services = [
	{
		icon: Box,
		title: 'Inventory Management System',
		description: 'Real-time tracking and automated inventory control for optimal stock levels.',
	},
	{
		icon: Shield,
		title: 'Customs Compliance Support',
		description: 'Expert guidance and documentation management for customs regulations.',
	},
	{
		icon: BarChart3,
		title: 'Weekly Entry Processing',
		description: 'Streamlined entry procedures to reduce merchandise processing fees.',
	},
	{
		icon: Truck,
		title: 'Zone-to-Zone Transfers',
		description: 'Efficient coordination of merchandise movement between FTZ locations.',
	},
	{
		icon: ClipboardCheck,
		title: 'Direct Delivery Processing',
		description: 'Expedited handling of direct delivery authorizations and procedures.',
	},
	{
		icon: PackageSearch,
		title: 'QC Inspection Services',
		description: 'Comprehensive quality control inspections and reporting.',
	},
	{
		icon: FileCheck,
		title: 'Assembly & Kitting',
		description: 'Value-added services for product assembly and custom kitting solutions.',
	},
	{
		icon: Thermometer,
		title: 'Temperature Controlled Storage',
		description: 'State-of-the-art climate-controlled facilities for sensitive goods.',
	},
	{
		icon: Globe2,
		title: 'Container Freight Station',
		description: 'Full-service container handling and distribution capabilities.',
	},
	{
		icon: Tags,
		title: 'Labeling & Repackaging',
		description: 'Custom labeling and packaging services to meet market requirements.',
	},
	{
		icon: Users2,
		title: 'Export Processing',
		description: 'Specialized handling and documentation for export shipments.',
	},
	{
		icon: Shield,
		title: 'Customs Broker Coordination',
		description: 'Seamless integration with customs brokers for efficient clearance.',
	},
];

export function FTZServices() {
	const [ref, inView] = useInView({
		triggerOnce: true,
		threshold: 0,
		rootMargin: '100px 0px',
	});

	return (
		<section className="py-24 bg-white">
			<div className="container mx-auto px-4 md:px-6">
				<div className="grid lg:grid-cols-2 gap-12 items-center">
					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('right')}
						className="max-w-xl"
					>
						<h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
							FTZ Services
						</h2>
						<h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
							Comprehensive FTZ Solutions
						</h3>

						<p className="text-gray-700 mb-8 text-lg">
							As an experienced Foreign-Trade Zone operator, we offer a complete suite
							of services to help you maximize the benefits of FTZ #41. Our team
							ensures smooth operations and full compliance with all FTZ regulations.
						</p>

						<div className="space-y-6 mb-8">
							<div className="flex items-center gap-4 p-4 bg-brand-50 rounded-lg border border-brand-100">
								<div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center text-brand-600">
									<CheckCircle2 size={24} />
								</div>
								<div>
									<h4 className="font-semibold text-gray-900">
										Full Compliance Guaranteed
									</h4>
									<p className="text-gray-600">
										Our expert team ensures adherence to all FTZ regulations
									</p>
								</div>
							</div>

							<div className="flex items-center gap-4 p-4 bg-brand-50 rounded-lg border border-brand-100">
								<div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center text-brand-600">
									<CheckCircle2 size={24} />
								</div>
								<div>
									<h4 className="font-semibold text-gray-900">24/7 Support</h4>
									<p className="text-gray-600">
										Round-the-clock assistance for your FTZ operations
									</p>
								</div>
							</div>
						</div>

						<Button
							asChild
							className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-6 text-lg"
						>
							<a href="/contact">Send Us a Message</a>
						</Button>
					</motion.div>

					<motion.div
						ref={ref}
						initial="hidden"
						animate={inView ? 'show' : 'hidden'}
						variants={fadeIn('left')}
						className="grid grid-cols-2 gap-4"
					>
						{services.map((service, index) => (
							<motion.div
								key={index}
								variants={fadeIn('up', index * 0.1)}
								className="p-4 bg-white rounded-lg border border-gray-200 hover:border-brand-200 hover:shadow-lg transition-all duration-300 group"
							>
								<div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center text-brand-600 group-hover:bg-brand-100 transition-colors mb-3">
									<service.icon size={20} />
								</div>
								<h4 className="font-semibold text-gray-900 mb-1 group-hover:text-brand-600 transition-colors">
									{service.title}
								</h4>
								<p className="text-sm text-gray-600">{service.description}</p>
							</motion.div>
						))}
					</motion.div>
				</div>
			</div>
		</section>
	);
}
